import { FunctionComponent, PropsWithChildren, Suspense, useContext } from 'react';
import { initReactI18next } from 'react-i18next';

import { logAnalyticsEvent } from '@vestico/utils';

import { i18n } from '@vestico/i18n';

import { TranslationsProvider } from './TranslationsProvider';
import { ConfigContext } from './WidgetEventsManager';

// i18n initialization
// eslint-disable-next-line @typescript-eslint/no-var-requires
const interpolationFormat = require('@vestico/i18n/src/interpolationFormat.cjs');

const isDev = process.env.NODE_ENV !== 'production';
const baseLocalesPath = 'public/locales/{{lng}}/{{ns}}.json';
const absoluteLocalesPath = !isDev ? `${process.env.WIDGET_PUBLIC_URL}${baseLocalesPath}` : `/${baseLocalesPath}`;

let didIniti18n = false;

interface i18nProps {
  lng?: string;
}

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

const initi18n = ({ lng }: i18nProps) => {
  i18n.use(initReactI18next).init(
    {
      fallbackLng: 'en',
      supportedLngs: ['en', 'de', 'es', 'fr', 'it', 'ca', 'nl'],
      debug: isDev,
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        format: interpolationFormat.format,
      },
      ns: ['translation', 'shared', 'model'],
      backend: {
        allowMultiLoading: false,
        crossDomain: true,
        loadPath: absoluteLocalesPath,
      },
      defaultNS: 'translation',
      detection: {
        lookupFromPathIndex: 0,
        order: ['querystring', 'cookie', 'htmlTag', 'navigator', 'path', 'subdomain'],
      },
      returnNull: false,
    },
    () => {
      // set the i18nn to a window variable
      if (isDev && window) {
        // skipcq JS-0303
        // eslint-disable-next-line dot-notation, @typescript-eslint/no-explicit-any
        (window as any)['i18next'] = i18n;
      }
    }
  );

  const language = lng ?? document.documentElement.lang;
  // Fix up wrongly formatted language codes (e.g. en_US -> en-US)
  const cleanLang = language?.replace('_', '-');

  if (cleanLang && cleanLang.length > 0) {
    i18n.changeLanguage(cleanLang);
  }
  return i18n;
};

export const InternationalizationLoader: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { language } = useContext(ConfigContext);

  if (!didIniti18n) {
    try {
      didIniti18n = true;
      initi18n({ lng: language });
    } catch (error) {
      console.error(error);
      logAnalyticsEvent({
        category: 'exception',
        event: `i18n-init`,
        name: error.message,
      });
    }
  }

  return (
    <Suspense fallback={null}>
      <TranslationsProvider>{children}</TranslationsProvider>
    </Suspense>
  );
};

export default InternationalizationLoader;
